import React from 'react';
import { useAudit } from '../context/AuditContext';
import { PageFrame } from '../components/PageFrame';
import { SectionCard } from '../components/SectionCard';
import { NavigationControls } from '../components/NavigationControls';
import { Users, Workflow, Settings, Database, CheckCircle } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

export default function WelcomePage() {
  const { auditData, updateCalculatorResult } = useAudit();
  const lenses = [
    { lens: 'People', focus: 'Who does the work, what needs expert judgment, and where handoffs slow the firm down.', icon: Users },
    { lens: 'Process', focus: 'How client intake, follow-up, documents, meetings, and reports move through the firm.', icon: Workflow },
    { lens: 'Technology', focus: 'Which tools support the firm and where people still rely on memory, email, or spreadsheets.', icon: Settings },
    { lens: 'Data', focus: 'What information is safe to use, what is confidential, and what needs human review.', icon: Database },
  ];

  return (
    <PageFrame pageNumber={2}>
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-extrabold uppercase text-secondary mb-4" data-testid="welcome-title">
              AI Opportunity Audit
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              You're about to discover where artificial intelligence may safely help your firm — clearly, practically, and without hype.
              This guided audit will help identify one responsible first pilot, what it may be worth, what client-trust risks to avoid, and what should not be automated yet.
            </p>
          </div>

          {/* Two-column layout */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: How to Use */}
            <SectionCard title="How to Use This Audit" className="h-fit">
              <ol className="space-y-4 text-foreground">
                <li className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    1
                  </div>
                  <div>
                    <strong className="text-secondary">Read</strong> each section carefully.
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    2
                  </div>
                  <div>
                    <strong className="text-secondary">Score</strong> each statement from 1 (Never True) to 5 (Always True).
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    3
                  </div>
                  <div>
                    <strong className="text-secondary">Review</strong> your recommended first pilot, value range, and risk guardrails in the final report.
                  </div>
                </li>
              </ol>
            </SectionCard>

            {/* Right: The Four Lenses */}
            <SectionCard title="The Four Audit Lenses">
              <Table>
                <TableHeader>
                  <TableRow className="border-accent/30">
                    <TableHead className="text-secondary font-bold">Lens</TableHead>
                    <TableHead className="text-secondary font-bold">What It Focuses On</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lenses.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <TableRow key={index} className="border-muted">
                        <TableCell className="font-semibold text-primary flex items-center gap-2">
                          <IconComponent className="h-5 w-5" strokeWidth={1.5} />
                          {item.lens}
                        </TableCell>
                        <TableCell className="text-foreground">{item.focus}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </SectionCard>
          </div>

          {/* Calculator bridge */}
          <SectionCard title="Did you use the quick calculator?" className="border-2 border-accent/40">
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Optional: paste your calculator estimate or result here. The calculator gives a rough number. This audit goes deeper so your recommendation is specific, safe, and useful.
              </p>
              <Label htmlFor="calculator-result">Calculator result or estimate (optional)</Label>
              <Textarea
                id="calculator-result"
                value={auditData.calculator_result || ''}
                onChange={(event) => updateCalculatorResult(event.target.value)}
                placeholder="Example: $18k-$42k/year in recoverable time, likely first workflow: client follow-up"
                className="min-h-24 resize-y"
                data-testid="calculator-result-textarea"
              />
            </div>
          </SectionCard>

          {/* Call to action message */}
          <div className="bg-accent/10 border-2 border-accent rounded-lg p-6 text-center">
            <CheckCircle className="h-8 w-8 text-accent mx-auto mb-3" />
            <p className="text-secondary font-semibold">
              Your responses are automatically saved as you go. Feel free to pause and return anytime.
            </p>
          </div>

          {/* Navigation */}
          <div className="pt-8">
            <NavigationControls currentPage={2} />
          </div>
        </div>
      </div>
    </PageFrame>
  );
}